import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Save, CheckCircle, XCircle, Loader2,
  Settings, Eye, EyeOff,
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RiskWeights {
  sanctions:      number;
  enforcement:    number;
  adverse_media:  number;
  pep:            number;
  consistency:    number;
  ato:            number;
}

interface ScreeningThresholds {
  sanctions_exact:          number;
  sanctions_close:          number;
  adverse_media_confidence: number;
  wealth_gap_multiplier:    number;
  max_enrichment_rounds:    number;
  edd_timeout_seconds:      number;
}

type TLP = 'WHITE' | 'GREEN' | 'AMBER' | 'RED';

interface ReportSettings {
  tlp:                   TLP;
  regulatory_basis:      string;
  auto_monitor_critical: boolean;
  language:              'en' | 'hi';
  footer_org:            string;
  footer_reg:            string;
  footer_contact:        string;
}

interface DataSource {
  id: string;
  name: string;
  status: 'ok' | 'degraded' | 'unavailable';
  last_call_mins: number;
  api_key?: string;
  endpoint_override?: string;
}

// ── Default State ─────────────────────────────────────────────────────────────

const DEFAULT_WEIGHTS: RiskWeights = {
  sanctions:     30,
  enforcement:   25,
  adverse_media: 20,
  pep:           15,
  consistency:   5,
  ato:           5,
};

const DEFAULT_THRESHOLDS: ScreeningThresholds = {
  sanctions_exact:          0.90,
  sanctions_close:          0.70,
  adverse_media_confidence: 0.75,
  wealth_gap_multiplier:    3.0,
  max_enrichment_rounds:    3,
  edd_timeout_seconds:      120,
};

const DEFAULT_REPORT: ReportSettings = {
  tlp:                   'AMBER',
  regulatory_basis:      'RBI KYC Master Direction 2016 · FATF Recommendation 10 · PMLA S.12',
  auto_monitor_critical: true,
  language:              'en',
  footer_org:            '',
  footer_reg:            '',
  footer_contact:        '',
};

const DATA_SOURCES_INITIAL: DataSource[] = [
  { id: 'opensanctions',   name: 'OpenSanctions',       status: 'ok',          last_call_mins: 3  },
  { id: 'occrp_aleph',     name: 'OCCRP Aleph',         status: 'ok',          last_call_mins: 7  },
  { id: 'leakosint',       name: 'LeakOSINT',           status: 'ok',          last_call_mins: 2  },
  { id: 'hudsonrock',      name: 'HudsonRock',          status: 'degraded',    last_call_mins: 15 },
  { id: 'google_news',     name: 'Google News CSE',     status: 'ok',          last_call_mins: 1  },
  { id: 'grok_nlp',        name: 'Grok NLP',            status: 'ok',          last_call_mins: 4  },
  { id: 'mca21',           name: 'MCA21',               status: 'ok',          last_call_mins: 5  },
  { id: 'vahan',           name: 'VAHAN API',           status: 'degraded',    last_call_mins: 22 },
  { id: 'gstn',            name: 'GSTN',                status: 'ok',          last_call_mins: 8  },
  { id: 'ecourts_njdg',    name: 'eCourts NJDG',        status: 'degraded',    last_call_mins: 18 },
  { id: 'instafinancials', name: 'InstaFinancials',     status: 'ok',          last_call_mins: 6  },
  { id: 'sebi_index',      name: 'SEBI Index',          status: 'ok',          last_call_mins: 11 },
  { id: 'sro_registry',    name: 'SRO Registry',        status: 'degraded',    last_call_mins: 31 },
  { id: 'wazirx',          name: 'WazirX',              status: 'ok',          last_call_mins: 3  },
  { id: 'cibil',           name: 'CIBIL',               status: 'unavailable', last_call_mins: 0  },
];

// ── Weight factor labels ──────────────────────────────────────────────────────

const WEIGHT_LABELS: Record<keyof RiskWeights, string> = {
  sanctions:     'Sanctions',
  enforcement:   'India Enforcement',
  adverse_media: 'Adverse Media',
  pep:           'PEP / UBO',
  consistency:   'Consistency',
  ato:           'ATO / Breach',
};

// ── TLP config ────────────────────────────────────────────────────────────────

const TLP_CONFIG: Record<TLP, { color: string; bg: string; border: string; desc: string }> = {
  WHITE:  { color: '#DCE8F5', bg: '#1A1A1A',     border: '#3A3A3A', desc: 'Unrestricted' },
  GREEN:  { color: '#00C853', bg: '#00C85318',    border: '#00C85340', desc: 'Community' },
  AMBER:  { color: '#F0B429', bg: '#F0B42918',    border: '#F0B42940', desc: 'Limited distribution' },
  RED:    { color: '#FF1F1F', bg: '#FF1F1F18',    border: '#FF1F1F40', desc: 'Restricted' },
};

// ── Status dot ────────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: DataSource['status'] }) {
  const cfg = {
    ok:          { color: '#00C853', label: 'Operational' },
    degraded:    { color: '#F0B429', label: 'Degraded'    },
    unavailable: { color: '#FF1F1F', label: 'Unavailable' },
  };
  const { color, label } = cfg[status];
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ background: color, boxShadow: `0 0 4px ${color}` }}
      />
      <span className="font-mono text-[9px]" style={{ color }}>{label}</span>
    </span>
  );
}

// ── Source Config Dialog ──────────────────────────────────────────────────────

function SourceConfigDialog({
  source,
  onClose,
  onSave,
}: {
  source: DataSource;
  onClose: () => void;
  onSave: (id: string, apiKey: string, endpoint: string) => void;
}) {
  const [apiKey, setApiKey]       = useState(source.api_key ?? '');
  const [endpoint, setEndpoint]   = useState(source.endpoint_override ?? '');
  const [showKey, setShowKey]     = useState(false);
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState<'ok' | 'fail' | null>(null);

  function handleTest() {
    setTesting(true);
    setTestResult(null);
    // Mock test — simulate a 1.5s delay then succeed based on source status
    setTimeout(() => {
      setTesting(false);
      setTestResult(source.status === 'unavailable' ? 'fail' : 'ok');
    }, 1500);
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent style={{ background: '#0C1118', border: '1px solid #182030', color: '#DCE8F5', maxWidth: 460 }}>
        <DialogHeader>
          <DialogTitle className="font-mono text-[12px] tracking-widest">{source.name} — Config</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          {/* API key */}
          <div className="flex flex-col gap-2">
            <Label className="font-mono text-[9px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>API KEY</Label>
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API key…"
                className="font-mono text-[11px] bg-[#090D13] border-[#182030] text-[#DCE8F5] pr-10 focus-visible:ring-0 focus-visible:border-[#00C8FF]"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showKey ? <EyeOff size={13} style={{ color: '#5A7A9A' }} /> : <Eye size={13} style={{ color: '#5A7A9A' }} />}
              </button>
            </div>
          </div>
          {/* Endpoint override */}
          <div className="flex flex-col gap-2">
            <Label className="font-mono text-[9px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>ENDPOINT OVERRIDE</Label>
            <Input
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="https://custom.endpoint.com/v1"
              className="font-mono text-[11px] bg-[#090D13] border-[#182030] text-[#DCE8F5] focus-visible:ring-0 focus-visible:border-[#00C8FF]"
            />
          </div>
          {/* Test connection */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleTest}
              disabled={testing}
              className="flex items-center gap-2 px-3 py-1.5 rounded font-mono text-[10px] font-bold tracking-wider transition-all disabled:opacity-50"
              style={{ background: '#0F1520', color: '#DCE8F5', border: '1px solid #182030' }}
            >
              {testing
                ? <><Loader2 size={11} className="animate-spin" /> Testing…</>
                : 'Test Connection'}
            </button>
            {testResult === 'ok'   && <div className="flex items-center gap-1.5"><CheckCircle size={13} style={{ color: '#00C853' }} /><span className="font-mono text-[10px]" style={{ color: '#00C853' }}>Connected</span></div>}
            {testResult === 'fail' && <div className="flex items-center gap-1.5"><XCircle size={13} style={{ color: '#FF1F1F' }} /><span className="font-mono text-[10px]" style={{ color: '#FF1F1F' }}>Failed</span></div>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="font-mono text-[10px] border-[#182030] text-[#5A7A9A] bg-transparent hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={() => { onSave(source.id, apiKey, endpoint); onClose(); }}
            className="font-mono text-[10px] bg-[#00C8FF18] text-[#00C8FF] border border-[#00C8FF30] hover:bg-[#00C8FF28]"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── SaveButton ────────────────────────────────────────────────────────────────

function SaveButton({ onClick, saving, saved }: { onClick: () => void; saving?: boolean; saved?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all disabled:opacity-50"
      style={{ background: saved ? '#00C85318' : '#00C8FF18', color: saved ? '#00C853' : '#00C8FF', border: `1px solid ${saved ? '#00C85330' : '#00C8FF30'}` }}
    >
      {saving ? <Loader2 size={11} className="animate-spin" /> : saved ? <CheckCircle size={11} /> : <Save size={11} />}
      {saved ? 'Saved' : 'Save'}
    </button>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10 }}
      className="p-5 flex flex-col gap-5"
    >
      <p className="text-[9px] font-mono tracking-[0.18em]" style={{ color: '#5A7A9A' }}>{title}</p>
      <Separator style={{ background: '#182030' }} />
      {children}
    </div>
  );
}

// ── RiskWeightSliders ─────────────────────────────────────────────────────────

function RiskWeightSliders() {
  const [weights, setWeights]     = useState<RiskWeights>(DEFAULT_WEIGHTS);
  const [locked, setLocked]       = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const totalOk = total === 100;

  function handleChange(key: keyof RiskWeights, val: number) {
    if (locked) return;
    setWeights((prev) => {
      const others = Object.keys(prev).filter((k) => k !== key) as (keyof RiskWeights)[];
      const remaining = 100 - val;
      const oldOthersTotal = others.reduce((sum, k) => sum + prev[k], 0);
      const next = { ...prev, [key]: val };
      if (oldOthersTotal === 0) {
        // distribute evenly
        const share = Math.floor(remaining / others.length);
        others.forEach((k, i) => {
          next[k] = i === others.length - 1 ? remaining - share * (others.length - 1) : share;
        });
      } else {
        // scale proportionally
        others.forEach((k) => {
          next[k] = Math.max(0, Math.round((prev[k] / oldOthersTotal) * remaining));
        });
        // fix rounding drift
        const currentTotal = Object.values(next).reduce((a, b) => a + b, 0);
        const drift = 100 - currentTotal;
        if (drift !== 0) {
          const lastKey = others[others.length - 1];
          next[lastKey] = Math.max(0, next[lastKey] + drift);
        }
      }
      return next;
    });
    setSaved(false);
  }

  function applyPreset(preset: 'rbi' | 'fatf' | 'custom') {
    if (preset === 'rbi') {
      setWeights({ sanctions: 20, enforcement: 30, adverse_media: 20, pep: 15, consistency: 10, ato: 5 });
      setLocked(false);
    } else if (preset === 'fatf') {
      setWeights({ sanctions: 35, enforcement: 20, adverse_media: 20, pep: 15, consistency: 5, ato: 5 });
      setLocked(false);
    } else {
      setLocked(false);
    }
    setSaved(false);
  }

  function handleSave() {
    if (!totalOk) return;
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); }, 800);
  }

  return (
    <Section title="RISK WEIGHT CONFIGURATION">
      {/* Presets */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-mono tracking-widest" style={{ color: '#3A5A7A' }}>INDIA PRESETS</span>
        {[
          { label: 'RBI Banking', preset: 'rbi' as const },
          { label: 'FATF Gulf',   preset: 'fatf' as const },
          { label: 'Custom',      preset: 'custom' as const },
        ].map(({ label, preset }) => (
          <button
            key={preset}
            onClick={() => applyPreset(preset)}
            className="px-3 py-1 rounded-full font-mono text-[9px] font-bold tracking-wider transition-all"
            style={{ background: '#090D13', color: '#5A7A9A', border: '1px solid #182030' }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Sliders */}
      <div className="flex flex-col gap-4">
        {(Object.keys(weights) as (keyof RiskWeights)[]).map((key) => (
          <div key={key} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] font-semibold" style={{ color: '#DCE8F5' }}>
                {WEIGHT_LABELS[key]}
              </span>
              <span className="font-mono text-[11px] font-bold" style={{ color: '#00C8FF' }}>
                {weights[key]}%
              </span>
            </div>
            <Slider
              min={0} max={100} step={1}
              value={[weights[key]]}
              onValueChange={([v]) => handleChange(key, v)}
              disabled={locked}
              className="[&>span]:bg-[#00C8FF] disabled:opacity-50"
            />
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] font-bold tracking-widest" style={{ color: '#5A7A9A' }}>
          TOTAL
        </span>
        <span
          className="font-mono text-[13px] font-bold"
          style={{ color: totalOk ? '#00C853' : '#FF1F1F' }}
        >
          {total}%
          {!totalOk && (
            <span className="ml-2 text-[9px]" style={{ color: '#FF1F1F' }}>
              (must equal 100)
            </span>
          )}
        </span>
      </div>

      <div className="flex justify-end">
        <SaveButton onClick={handleSave} saving={saving} saved={saved} />
      </div>
    </Section>
  );
}

// ── ThresholdInputs ───────────────────────────────────────────────────────────

interface ThresholdField {
  key: keyof ScreeningThresholds;
  label: string;
  step: number;
  min: number;
  max: number;
  description: string;
}

const THRESHOLD_FIELDS: ThresholdField[] = [
  { key: 'sanctions_exact',          label: 'Sanctions EXACT match',          step: 0.01, min: 0.5, max: 1.0, description: 'Min score for exact match (0–1)' },
  { key: 'sanctions_close',          label: 'Sanctions CLOSE match',          step: 0.01, min: 0.3, max: 1.0, description: 'Min score for close match (0–1)' },
  { key: 'adverse_media_confidence', label: 'Adverse media min confidence',   step: 0.01, min: 0.3, max: 1.0, description: 'Filter articles below this confidence' },
  { key: 'wealth_gap_multiplier',    label: 'Wealth gap alert multiplier',    step: 0.5,  min: 1.0, max: 20,  description: 'Alert when wealth > N × declared income' },
  { key: 'max_enrichment_rounds',    label: 'Max enrichment rounds',          step: 1,    min: 1,   max: 10,  description: 'Maximum CDD graph expansion rounds' },
  { key: 'edd_timeout_seconds',      label: 'EDD timeout (seconds)',          step: 10,   min: 30,  max: 600, description: 'Hard timeout for EDD pipeline' },
];

function ThresholdInputs() {
  const [vals, setVals]     = useState<ScreeningThresholds>(DEFAULT_THRESHOLDS);
  const [errors, setErrors] = useState<Partial<Record<keyof ScreeningThresholds, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  function handleChange(key: keyof ScreeningThresholds, raw: string) {
    const n = parseFloat(raw);
    const field = THRESHOLD_FIELDS.find((f) => f.key === key);
    if (!field) return;
    if (isNaN(n)) {
      setErrors((e) => ({ ...e, [key]: 'Must be a number' }));
    } else if (n < field.min || n > field.max) {
      setErrors((e) => ({ ...e, [key]: `Must be ${field.min}–${field.max}` }));
    } else {
      setErrors((e) => { const next = { ...e }; delete next[key]; return next; });
      setVals((v) => ({ ...v, [key]: n }));
    }
    setSaved(false);
  }

  function handleSave() {
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); }, 800);
  }

  return (
    <Section title="SCREENING THRESHOLDS">
      <div className="flex flex-col gap-4">
        {THRESHOLD_FIELDS.map((f) => (
          <div key={f.key} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="font-mono text-[11px]" style={{ color: '#DCE8F5' }}>{f.label}</Label>
              {errors[f.key] && (
                <span className="font-mono text-[9px]" style={{ color: '#FF1F1F' }}>{errors[f.key]}</span>
              )}
            </div>
            <input
              type="number"
              defaultValue={vals[f.key]}
              step={f.step}
              min={f.min}
              max={f.max}
              onChange={(e) => handleChange(f.key, e.target.value)}
              className="w-32 px-3 py-1.5 rounded-lg font-mono text-[12px] outline-none transition-all"
              style={{
                background: '#090D13',
                border: `1px solid ${errors[f.key] ? '#FF1F1F40' : '#182030'}`,
                color: '#DCE8F5',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#00C8FF'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = errors[f.key] ? '#FF1F1F40' : '#182030'; }}
            />
            <span className="font-mono text-[9px]" style={{ color: '#3A5A7A' }}>{f.description}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <SaveButton onClick={handleSave} saving={saving} saved={saved} />
      </div>
    </Section>
  );
}

// ── DataSourceStatusGrid ──────────────────────────────────────────────────────

function DataSourceStatusGrid() {
  const [sources, setSources] = useState<DataSource[]>(DATA_SOURCES_INITIAL);
  const [configTarget, setConfigTarget] = useState<DataSource | null>(null);

  function handleSaveConfig(id: string, apiKey: string, endpoint: string) {
    setSources((prev) =>
      prev.map((s) => s.id === id ? { ...s, api_key: apiKey, endpoint_override: endpoint } : s)
    );
  }

  return (
    <Section title="DATA SOURCE STATUS">
      <div className="flex flex-col gap-0">
        {sources.map((src, idx) => (
          <div
            key={src.id}
            className="flex items-center justify-between px-3 py-2.5 transition-colors hover:bg-white/[0.02]"
            style={{
              borderBottom: idx < sources.length - 1 ? '1px solid #0F1928' : undefined,
              borderRadius: idx === 0 ? '6px 6px 0 0' : idx === sources.length - 1 ? '0 0 6px 6px' : undefined,
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <StatusDot status={src.status} />
              <span className="font-mono text-[11px] truncate" style={{ color: '#DCE8F5' }}>{src.name}</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {src.status !== 'unavailable' ? (
                <span className="font-mono text-[9px]" style={{ color: '#3A5A7A' }}>
                  {src.last_call_mins === 0 ? 'never' : `${src.last_call_mins}m ago`}
                </span>
              ) : (
                <span className="font-mono text-[9px]" style={{ color: '#FF1F1F' }}>unavailable</span>
              )}
              <button
                onClick={() => setConfigTarget(src)}
                className="p-1 rounded transition-colors hover:bg-white/5"
              >
                <Settings size={12} style={{ color: '#5A7A9A' }} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {configTarget && (
        <SourceConfigDialog
          source={configTarget}
          onClose={() => setConfigTarget(null)}
          onSave={handleSaveConfig}
        />
      )}
    </Section>
  );
}

// ── ReportSettingsForm ────────────────────────────────────────────────────────

function ReportSettingsForm() {
  const [settings, setSettings] = useState<ReportSettings>(DEFAULT_REPORT);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);

  function update<K extends keyof ReportSettings>(key: K, val: ReportSettings[K]) {
    setSettings((prev) => ({ ...prev, [key]: val }));
    setSaved(false);
  }

  function handleSave() {
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); }, 800);
  }

  const TLP_OPTIONS: TLP[] = ['WHITE', 'GREEN', 'AMBER', 'RED'];

  return (
    <Section title="REPORT DEFAULTS">
      <div className="flex flex-col gap-5">

        {/* TLP */}
        <div className="flex flex-col gap-2">
          <Label className="font-mono text-[9px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>TLP CLASSIFICATION</Label>
          <div className="flex gap-2 flex-wrap">
            {TLP_OPTIONS.map((t) => {
              const { color, bg, border, desc } = TLP_CONFIG[t];
              const active = settings.tlp === t;
              return (
                <button
                  key={t}
                  onClick={() => update('tlp', t)}
                  title={desc}
                  className="px-3 py-1.5 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all"
                  style={{
                    background: active ? bg : '#090D13',
                    color: active ? color : '#5A7A9A',
                    border: `1px solid ${active ? border : '#182030'}`,
                  }}
                >
                  TLP:{t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Regulatory basis */}
        <div className="flex flex-col gap-2">
          <Label className="font-mono text-[9px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>REGULATORY BASIS</Label>
          <textarea
            value={settings.regulatory_basis}
            onChange={(e) => update('regulatory_basis', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg font-mono text-[11px] outline-none resize-none transition-all"
            style={{ background: '#090D13', border: '1px solid #182030', color: '#DCE8F5' }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#1E2B3F'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#182030'; }}
          />
        </div>

        {/* Auto-monitor critical */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[11px] font-semibold" style={{ color: '#DCE8F5' }}>Auto-monitor CRITICAL hits</p>
            <p className="font-mono text-[9px] mt-0.5" style={{ color: '#3A5A7A' }}>Automatically add CRITICAL-tier subjects to continuous monitoring</p>
          </div>
          <Switch
            checked={settings.auto_monitor_critical}
            onCheckedChange={(v) => update('auto_monitor_critical', v)}
          />
        </div>

        {/* PDF language */}
        <div className="flex flex-col gap-2">
          <Label className="font-mono text-[9px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>PDF LANGUAGE</Label>
          <div className="flex gap-2">
            <button
              onClick={() => update('language', 'en')}
              className="px-4 py-1.5 rounded-lg font-mono text-[10px] font-bold tracking-widest transition-all"
              style={{
                background: settings.language === 'en' ? '#00C8FF18' : '#090D13',
                color: settings.language === 'en' ? '#00C8FF' : '#5A7A9A',
                border: `1px solid ${settings.language === 'en' ? '#00C8FF30' : '#182030'}`,
              }}
            >
              English
            </button>
            <button
              disabled
              title="Coming soon"
              className="px-4 py-1.5 rounded-lg font-mono text-[10px] font-bold tracking-widest opacity-40 cursor-not-allowed"
              style={{ background: '#090D13', color: '#5A7A9A', border: '1px solid #182030' }}
            >
              Hindi
              <span
                className="ml-2 text-[8px] font-mono px-1.5 py-0.5 rounded"
                style={{ background: '#F0B42918', color: '#F0B429', border: '1px solid #F0B42930' }}
              >
                soon
              </span>
            </button>
          </div>
        </div>

        {/* Report footer */}
        <div className="flex flex-col gap-3">
          <Label className="font-mono text-[9px] tracking-[0.14em]" style={{ color: '#5A7A9A' }}>REPORT FOOTER</Label>
          {([
            { field: 'footer_org' as const,     placeholder: 'Organization name' },
            { field: 'footer_reg' as const,     placeholder: 'Registration / licence number' },
            { field: 'footer_contact' as const, placeholder: 'Contact email / phone' },
          ]).map(({ field, placeholder }) => (
            <input
              key={field}
              type="text"
              value={settings[field]}
              onChange={(e) => update(field, e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 rounded-lg font-mono text-[11px] outline-none transition-all"
              style={{ background: '#090D13', border: '1px solid #182030', color: '#DCE8F5' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#1E2B3F'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#182030'; }}
            />
          ))}
        </div>

        <div className="flex justify-end">
          <SaveButton onClick={handleSave} saving={saving} saved={saved} />
        </div>
      </div>
    </Section>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ProfilerSettings() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ background: '#050709', minHeight: '100vh' }}
      className="flex flex-col"
    >
      {/* Header */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }} className="px-6 py-4">
        <div className="flex items-center gap-3">
          <Settings size={16} style={{ color: '#5A7A9A' }} />
          <div>
            <h1 className="text-[13px] font-mono font-bold tracking-[0.22em]" style={{ color: '#DCE8F5' }}>
              PROFILER SETTINGS
            </h1>
            <p className="text-[10px] font-mono mt-0.5" style={{ color: '#5A7A9A' }}>
              Risk engine config · Data sources · Report defaults
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 px-6 py-6">

        {/* Left column */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="flex-1 flex flex-col gap-5 min-w-0"
        >
          <RiskWeightSliders />
          <ThresholdInputs />
        </motion.div>

        {/* Right column */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="flex-1 flex flex-col gap-5 min-w-0"
        >
          <DataSourceStatusGrid />
          <ReportSettingsForm />
        </motion.div>
      </div>
    </motion.div>
  );
}
